import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/lib/api';
import type { IntakeListResponse, OscarIntake } from '@/types/intake';

/**
 * useIntakePolling — best-effort near-real-time notifications for new Oscar intakes.
 *
 * There is no WebSocket, so we poll every 60s:
 *   GET /api/projects?status=pending_approval&source=oscar&since={lastCheck}
 * Any intake that arrived after `lastCheck` fires a Sonner toast with a "View" action that
 * navigates to the Intakes tab. `lastCheck` is persisted in localStorage and seeded on first mount
 * (so the existing backlog is never toasted). Only runs for leadership/admin roles.
 */

const LAST_CHECK_KEY = 'oscar-intake-last-check';
const POLL_INTERVAL_MS = 60_000;

export function useIntakePolling(): void {
  const { user, isAuthenticated } = useAuth();
  const client = useApiClient();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timerRef = useRef<number | null>(null);

  const canSee =
    isAuthenticated && (user?.role === 'leadership' || user?.role === 'admin');

  useEffect(() => {
    if (!canSee) return;

    // Seed the watermark on first mount so we don't announce the entire existing backlog.
    if (!localStorage.getItem(LAST_CHECK_KEY)) {
      localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
    }

    let cancelled = false;

    const poll = async (): Promise<void> => {
      const lastCheck = localStorage.getItem(LAST_CHECK_KEY) ?? new Date().toISOString();
      try {
        const params = new URLSearchParams({
          status: 'pending_approval',
          source: 'oscar',
          since: lastCheck,
        });
        const response = await client.get<IntakeListResponse>(
          `/api/projects?${params.toString()}`,
          { _suppressErrorToast: true }
        );
        if (cancelled) return;

        const intakes: OscarIntake[] = response.data.projects ?? [];
        const lastCheckMs = new Date(lastCheck).getTime();

        // Defensive client-side filter in case the backend ignores `since`.
        const fresh = intakes.filter((intake) => {
          const ts = intake.intake_received_at ?? intake.approval_requested_at ?? null;
          if (!ts) return true;
          return new Date(ts).getTime() > lastCheckMs;
        });

        // Advance the watermark regardless of results.
        localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());

        if (fresh.length > 0) {
          // Invalidate the intakes cache so the Intakes table refreshes immediately
          // without the user needing to manually reload the page.
          void queryClient.invalidateQueries({ queryKey: ['intakes'] });
        }

        for (const intake of fresh) {
          const title = intake.company_name
            ? `${intake.company_name}${intake.deal_name ? ` — ${intake.deal_name}` : ''}`
            : intake.title;
          toast(`🆕 New intake: ${title} from Oscar`, {
            action: {
              label: 'View',
              onClick: () => navigate('/intakes'),
            },
          });
        }
      } catch {
        // Best-effort — a failed poll must never disrupt the app.
      }
    };

    void poll();
    timerRef.current = window.setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, [canSee, client, navigate]);
}
